import imageCompression from 'browser-image-compression'
import { supabase } from '@/lib/supabase'
import type { Profile } from '@/models'

// ── Sign Up ───────────────────────────────────────────────────
export async function signUp(
  email: string,
  password: string,
  fullName: string,
  role: 'booker' | 'host' = 'booker',
  registrationData?: { phone?: string; business_company_name?: string; abn?: string }
): Promise<{ error: Error | null }> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName,
        role,
      },
    },
  })

  if (error) return { error: new Error(error.message) }

  // Insert profile row if session is immediately available (email confirmation disabled)
  if (data.session && data.user) {
    const profileRow: Record<string, unknown> = {
      id: data.user.id,
      full_name: fullName,
      email,
      role: 'USER',
      is_host: role === 'host',
      is_verified: false,
      host_status: 'PENDING_VERIFICATION',
    }
    if (registrationData?.phone) profileRow.phone = registrationData.phone
    if (registrationData?.business_company_name) profileRow.business_company_name = registrationData.business_company_name
    if (registrationData?.abn) profileRow.abn = registrationData.abn
    const { error: profileError } = await supabase.from('profiles').insert(profileRow)
    if (profileError) {
      console.error('[signUp] Profile creation failed:', profileError)
    }
  }

  return { error: null }
}

// ── Sign In ───────────────────────────────────────────────────
export async function signIn(
  email: string,
  password: string
): Promise<{ error: Error | null }> {
  const { error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) return { error: new Error(error.message) }
  return { error: null }
}

// ── Sign Out ──────────────────────────────────────────────────
export async function signOut(): Promise<void> {
  await supabase.auth.signOut()
}

// ── Send Password Reset Email ─────────────────────────────────
export async function sendPasswordReset(email: string): Promise<{ error: Error | null }> {
  const redirectTo = `${import.meta.env.VITE_APP_URL || window.location.origin}/reset-password`
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo })
  if (error) return { error: new Error(error.message) }
  return { error: null }
}

// ── Update Password ────────────────────────────────────────────
export async function updatePassword(newPassword: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.auth.updateUser({ password: newPassword })
  if (error) return { error: new Error(error.message) }
  // Sign out after password reset so user logs in fresh
  await supabase.auth.signOut()
  return { error: null }
}

// ── Ensure Profile (RPC) ──────────────────────────────────────
export async function ensureProfile(
  fullName: string = '',
  role: 'booker' | 'host' = 'booker'
): Promise<Profile | null> {
  try {
    const { data, error } = await supabase.rpc('ensure_profile', {
      p_full_name: fullName,
      p_role: role,
    })

    if (error) throw error

    if (data?.success && data?.profile) {
      return await getProfile(data.profile.id as string)
    }
    return null
  } catch {
    // Fallback: direct upsert if RPC not available
    return await upsertProfileDirect(fullName, role)
  }
}

async function upsertProfileDirect(
  fullName: string,
  role: 'booker' | 'host'
): Promise<Profile | null> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  const dbRole = role === 'host' ? 'HOST' : 'USER'
  const { data, error } = await supabase
    .from('profiles')
    .upsert({
      id: user.id,
      role: dbRole,
      full_name: fullName || user.email?.split('@')[0] || '',
      email: user.email || '',
      created_at: new Date().toISOString(),
    })
    .select()
    .single()

  if (error) return null
  return data as Profile
}

// ── Get Profile ───────────────────────────────────────────────
export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle()

  if (error || !data) return null
  return data as Profile
}

// ── Update Profile ────────────────────────────────────────────
export async function updateProfile(
  userId: string,
  updates: Partial<Pick<Profile, 'full_name' | 'phone' | 'business_name' | 'avatar_url' | 'suburb' | 'role'>>
): Promise<{ data: Profile | null; error: Error | null }> {
  const { data, error } = await supabase
    .from('profiles')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', userId)
    .select()
    .single()

  if (error) return { data: null, error: new Error(error.message) }
  return { data: data as Profile, error: null }
}

// ── Become Host (RPC) ─────────────────────────────────────────
export async function becomeHost(
  phoneNumber: string,
  businessName: string,
  suburb: string
): Promise<{ success: boolean; error?: string; profile?: Profile }> {
  try {
    const { data, error } = await supabase.rpc('become_host', {
      p_phone_number: phoneNumber,
      p_business_name: businessName || '',
      p_suburb: suburb,
    })

    if (error) throw error

    if (data?.success === true) {
      const { data: { user } } = await supabase.auth.getUser()
      const profile = user ? await getProfile(user.id) : null
      return { success: true, profile: profile ?? undefined }
    }

    return { success: false, error: data?.error ?? 'Unknown error' }
  } catch {
    // Fallback: direct profile update
    return await becomeHostFallback(phoneNumber, businessName, suburb)
  }
}

async function becomeHostFallback(
  phoneNumber: string,
  businessName: string,
  suburb: string
): Promise<{ success: boolean; error?: string; profile?: Profile }> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return { success: false, error: 'Not logged in' }

  const updates: Record<string, unknown> = {
    is_host: true,
    phone: phoneNumber,
    suburb,
    updated_at: new Date().toISOString(),
  }
  if (businessName) updates.business_name = businessName

  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', user.id)
    .select()
    .single()

  if (error) return { success: false, error: error.message }
  return { success: true, profile: data as Profile }
}

// ── Update Insurance Declaration ──────────────────────────────
export async function updateProfileInsurance(
  userId: string,
  type: 'host' | 'renter'
): Promise<{ error: Error | null }> {
  const now = new Date().toISOString()
  const updates =
    type === 'host'
      ? { host_insurance_declared: true, host_insurance_declared_at: now, updated_at: now }
      : { renter_insurance_declared: true, renter_insurance_declared_at: now, updated_at: now }

  const { error } = await supabase.from('profiles').update(updates).eq('id', userId)
  if (error) return { error: new Error(error.message) }
  return { error: null }
}

// ── Become Host with ABN (direct update) ──────────────────────
export async function becomeHostWithABN(
  userId: string,
  data: { abn: string; business_company_name: string }
): Promise<{ success: boolean; error?: string }> {
  const now = new Date().toISOString()
  const { error } = await supabase
    .from('profiles')
    .update({
      is_host: true,
      host_status: 'PENDING_VERIFICATION',
      abn: data.abn,
      business_company_name: data.business_company_name,
      host_insurance_declared: true,
      host_insurance_declared_at: now,
      updated_at: now,
    })
    .eq('id', userId)

  if (error) return { success: false, error: error.message }
  return { success: true }
}

// ── Upload Avatar ─────────────────────────────────────────────
// Avatars display at 80px at most, so shrink the source (often a multi-MB phone
// photo) before uploading instead of serving it full size to every viewer.
// Mirrors photoService.compressImage, with tighter limits.
async function compressAvatar(file: File): Promise<File> {
  try {
    return await imageCompression(file, {
      maxSizeMB: 0.2,
      maxWidthOrHeight: 512,
      useWebWorker: true,
      fileType: 'image/jpeg',
    })
  } catch {
    // Never block the upload on a compression failure.
    return file
  }
}

export async function uploadAvatar(
  userId: string,
  file: File
): Promise<{ url: string | null; error: Error | null }> {
  const compressed = await compressAvatar(file)
  // Output is always JPEG now, so the extension and content type say so — the
  // previous version kept the source extension and mislabelled the bytes.
  const path = `${userId}/avatar.jpg`

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(path, compressed, { upsert: true, contentType: 'image/jpeg' })

  if (uploadError) return { url: null, error: new Error(uploadError.message) }

  const { data } = supabase.storage.from('avatars').getPublicUrl(path)
  // The storage path never changes under upsert, so without a cache-buster the
  // CDN keeps serving the old avatar after a re-upload.
  return { url: `${data.publicUrl}?t=${Date.now()}`, error: null }
}
